import PlanPackageModule from '@/models/plans/PlanPackageModule'
import Module from '@/models/plans/Module'

/**
 * Service para gerenciar relações entre módulos
 * Lida com: Dependencies, Combos, Courtesy
 */
class ModuleRelations {
    /**
     * Processa dependências de um módulo quando ele é selecionado
     * @param {Object} module - Módulo que foi selecionado
     * @param {Array} packageModules - Módulos disponíveis no pacote
     * @param {Array} optionalModules - Módulos opcionais disponíveis
     * @returns {Array} Lista de módulos dependentes que devem ser marcados
     */
    processDependencies(module, packageModules = [], optionalModules = []) {
        if (!module.dependency || !Array.isArray(module.dependency)) {
            return []
        }

        const dependentModules = []

        module.dependency.forEach(dependency => {
            // Primeiro tentar encontrar nos optionalModules
            let dependentModule = optionalModules.find(
                moduleDependency =>
                    moduleDependency.module.external_id == dependency.module_id,
            )

            // Se não encontrou, tentar nos módulos do pacote
            if (!dependentModule) {
                dependentModule = packageModules.find(
                    moduleDependency =>
                        moduleDependency.module.external_id == dependency.module_id,
                )
            }

            if (dependentModule) {
                dependentModules.push({
                    module: dependentModule,
                    shouldCheck: true,
                    shouldLock: true,
                    addAmount: 1,
                })
            }
        })

        return dependentModules
    }

    /**
     * Libera dependências de um módulo quando ele é desmarcado
     * @param {Object} module - Módulo que foi desmarcado
     * @param {Array} packageModules - Módulos disponíveis no pacote
     * @param {Array} optionalModules - Módulos opcionais disponíveis
     * @param {Array} selectedModules - Módulos já selecionados
     * @returns {Array} Lista de módulos que devem ser desbloqueados
     */
    releaseDependencies(
        module,
        packageModules = [],
        optionalModules = [],
        selectedModules = [],
    ) {
        if (!module.dependency || !Array.isArray(module.dependency)) {
            return []
        }

        const modulesToUnlock = []

        module.dependency.forEach(dependency => {
            // Encontrar o módulo dependente
            let dependentModule = optionalModules.find(
                moduleDependency =>
                    moduleDependency.module.external_id == dependency.module_id,
            )

            if (!dependentModule) {
                dependentModule = packageModules.find(
                    moduleDependency =>
                        moduleDependency.module.external_id == dependency.module_id,
                )
            }

            if (dependentModule) {
                // Verificar se está nos módulos selecionados
                const existingModuleIndex = selectedModules.findIndex(
                    moduleChecked =>
                        moduleChecked.module_id === dependentModule.module_id,
                )

                if (existingModuleIndex !== -1) {
                    modulesToUnlock.push({
                        module: dependentModule,
                        indexInSelected: existingModuleIndex,
                    })
                }
            }
        })

        return modulesToUnlock
    }

    /**
     * Cria módulos de combo quando um módulo é selecionado
     * @param {Object} module - Módulo que possui combos
     * @param {Number} planPackageId - ID do pacote de plano
     * @param {Number} currentLength - Comprimento atual da lista (para order)
     * @returns {Array} Lista de módulos de combo criados
     */
    createComboModules(module, planPackageId, currentLength = 0) {
        if (!module.combo || !Array.isArray(module.combo)) {
            return []
        }

        const comboModules = []
        let order = currentLength

        module.combo.forEach(item => {
            const comboModuleId =
                item.id != null && item.id !== ''
                    ? item.id
                    : item.module_id

            order++
            const comboModule = new PlanPackageModule({
                plan_package_id: planPackageId,
                module_id: comboModuleId,
                external_id: item.module_id || item.external_id,
                price: item.price,
                price_type: item.price_type,
                add_on: true,
                module: new Module({
                    id: comboModuleId,
                    external_id: item.module_id || item.external_id,
                    vindi_id: item.external_id,
                    name: item.name,
                    price: item.price,
                    price_type: item.price_type,
                }),
                add_amount: 0,
                checked: false,
                locked: false,
                order: order,
            })

            comboModules.push(comboModule)
        })

        return comboModules
    }

    /**
     * Remove módulos de combo quando um módulo é desmarcado
     * @param {Object} module - Módulo que possui combos
     * @param {Array} comboItems - Lista atual de combos
     * @param {Array} selectedModules - Módulos selecionados
     * @returns {Object} { comboItemsToRemove, selectedModulesToRemove }
     */
    removeComboModules(module, comboItems = [], selectedModules = []) {
        if (!module.combo || !Array.isArray(module.combo)) {
            return { comboItemsToRemove: [], selectedModulesToRemove: [] }
        }

        const comboItemsToRemove = []
        const selectedModulesToRemove = []

        module.combo.forEach(item => {
            // Encontrar combo na lista
            const comboIndex = comboItems.findIndex(
                comboItem => comboItem.module_id === item.id,
            )
            if (comboIndex !== -1) {
                comboItemsToRemove.push(comboIndex)
            }

            // Encontrar nos selecionados
            const selectedIndex = selectedModules.findIndex(
                moduleChecked => moduleChecked.module_id === item.id,
            )
            if (selectedIndex !== -1) {
                selectedModulesToRemove.push(selectedIndex)
            }
        })

        return { comboItemsToRemove, selectedModulesToRemove }
    }

    /**
     * Processa módulos de cortesia quando um módulo é selecionado
     * @param {Object} module - Módulo que possui cortesia
     * @param {Array} selectedModules - Módulos já selecionados
     * @returns {Array} Lista de módulos de cortesia para adicionar/atualizar
     */
    processCourtesyModules(module, selectedModules = []) {
        if (!module.courtesy || !Array.isArray(module.courtesy)) {
            return []
        }

        const courtesyActions = []

        module.courtesy.forEach(courtesy => {
            const existingModuleIndex = selectedModules.findIndex(
                moduleChecked => moduleChecked.module_id === courtesy.module_id,
            )

            if (existingModuleIndex !== -1) {
                // Atualizar módulo existente
                const courtesyAmount = courtesy.price_type !== 'flat' ? (module.amount + (module.add_amount || 0)) : 1
                courtesyActions.push({
                    action: 'update',
                    index: existingModuleIndex,
                    data: {
                        add_amount: courtesyAmount,
                        amount: courtesyAmount,
                    },
                })
            } else {
                // Adicionar novo módulo de cortesia
                const courtesyAmount = courtesy.price_type !== 'flat' ? (module.amount + (module.add_amount || 0)) : 1
                const courtesyModule = {
                    module_id: courtesy.module_id,
                    external_id: courtesy.module_id,
                    name: courtesy.name,
                    module: {
                        id: courtesy.module_id,
                        external_id: courtesy.module_id,
                        vindi_id: courtesy.external_id,
                        name: courtesy.name,
                    },
                    price: courtesy.price || 0,
                    price_type: courtesy.price_type,
                    amount: courtesyAmount,
                    add_amount: courtesyAmount,
                    checked: true,
                    locked: true,
                    order: module.order || 0,
                    third_party: courtesy.third_party || false,
                }

                courtesyActions.push({
                    action: 'add',
                    data: courtesyModule,
                })
            }
        })

        return courtesyActions
    }

    /**
     * Remove módulos de cortesia quando um módulo é desmarcado
     * @param {Object} module - Módulo que possui cortesia
     * @param {Array} selectedModules - Módulos selecionados
     * @param {Array} fixedModules - Módulos fixos do pacote
     * @returns {Array} Índices dos módulos a remover
     */
    removeCourtesyModules(module, selectedModules = [], fixedModules = []) {
        if (!module.courtesy || !Array.isArray(module.courtesy)) {
            return []
        }

        const indicesToRemove = []

        module.courtesy.forEach(courtesy => {
            const existingModuleIndex = selectedModules.findIndex(
                moduleChecked => moduleChecked.module_id === courtesy.module_id,
            )

            const fixedModule = fixedModules.find(
                moduleFixed => moduleFixed.module_id === courtesy.module_id
            )

            // Só remove se não for módulo fixo
            if (existingModuleIndex !== -1 && !fixedModule) {
                indicesToRemove.push(existingModuleIndex)
            }
        })

        return indicesToRemove
    }
}

// Exportar instância singleton
const moduleRelations = new ModuleRelations()

export default moduleRelations

// Exportar também a classe para testes
export { ModuleRelations }